import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Save, Loader2, Star, MessageSquare, Check, Ban } from 'lucide-react';
import './AdminReviews.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    role: '',
    review_text: '',
    rating: 5,
    profile_image: '',
    approved: false
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingReview(null);
    setFormData({
      client_name: '',
      role: '',
      review_text: '',
      rating: 5,
      profile_image: '',
      approved: false
    });
    setShowModal(true);
  };

  const openEditModal = (review) => {
    setEditingReview(review);
    setFormData({
      client_name: review.client_name,
      role: review.role || '',
      review_text: review.review_text,
      rating: review.rating,
      profile_image: review.profile_image || '',
      approved: review.approved
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingReview 
        ? `${API_URL}/api/admin/reviews/${editingReview.id}`
        : `${API_URL}/api/admin/reviews`;
      
      const response = await fetch(url, {
        method: editingReview ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchReviews();
        setShowModal(false);
      }
    } catch (error) {
      console.error('Failed to save review:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleApproval = async (review) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/reviews/${review.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ approved: !review.approved })
      });
      fetchReviews();
    } catch (error) {
      console.error('Failed to toggle approval:', error);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchReviews();
      }
    } catch (error) {
      console.error('Failed to delete review:', error);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} size={14} fill={i < rating ? '#f59e0b' : 'none'} color={i < rating ? '#f59e0b' : '#d1d5db'} />
    ));
  };

  return (
    <div className="admin-reviews" data-testid="admin-reviews">
      <div className="page-header">
        <div>
          <h1>Reviews</h1>
          <p>Manage client testimonials</p>
        </div>
        <button className="add-btn" onClick={openCreateModal} data-testid="add-review-btn">
          <Plus size={18} />
          <span>Add Review</span>
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <Loader2 className="spin" size={32} />
          <span>Loading reviews...</span>
        </div>
      ) : reviews.length === 0 ? (
        <div className="empty-container">
          <MessageSquare size={48} />
          <h3>No reviews yet</h3>
          <p>Add client testimonials to showcase on your website.</p>
          <button className="add-btn" onClick={openCreateModal}>
            <Plus size={18} />
            <span>Add Review</span>
          </button>
        </div>
      ) : (
        <div className="reviews-grid">
          {reviews.map((review) => (
            <div key={review.id} className={`review-card ${review.approved ? 'approved' : 'pending'}`} data-testid={`review-card-${review.id}`}>
              <div className="review-header">
                <div className="reviewer-info">
                  {review.profile_image ? (
                    <img src={review.profile_image} alt={review.client_name} className="reviewer-avatar" />
                  ) : (
                    <div className="reviewer-avatar placeholder">{review.client_name.charAt(0)}</div>
                  )}
                  <div>
                    <h3 className="reviewer-name">{review.client_name}</h3>
                    {review.role && <span className="reviewer-role">{review.role}</span>}
                  </div>
                </div>
                <span className={`status-badge ${review.approved ? 'approved' : 'pending'}`}>
                  {review.approved ? 'Approved' : 'Pending'}
                </span>
              </div>
              <div className="review-rating">{renderStars(review.rating)}</div>
              <p className="review-text">"{review.review_text}"</p>
              <div className="review-actions">
                <button className="action-btn" onClick={() => toggleApproval(review)} title={review.approved ? 'Unapprove' : 'Approve'}>
                  {review.approved ? <Ban size={16} /> : <Check size={16} />}
                  {review.approved ? 'Unapprove' : 'Approve'}
                </button>
                <button className="action-btn" onClick={() => openEditModal(review)}>
                  <Edit2 size={16} /> Edit
                </button>
                <button className="action-btn delete" onClick={() => deleteReview(review.id)}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingReview ? 'Edit Review' : 'Add Review'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="review-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Client Name *</label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Role/Company</label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    placeholder="Wedding Photographer"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Profile Image URL</label>
                <input
                  type="url"
                  value={formData.profile_image}
                  onChange={(e) => setFormData({ ...formData, profile_image: e.target.value })}
                  placeholder="https://example.com/photo.jpg"
                />
              </div>

              <div className="form-group">
                <label>Rating *</label>
                <div className="rating-selector">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      type="button"
                      key={num}
                      className={`rating-btn ${formData.rating >= num ? 'active' : ''}`}
                      onClick={() => setFormData({ ...formData, rating: num })}
                    >
                      <Star size={24} fill={formData.rating >= num ? '#f59e0b' : 'none'} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Review Text *</label>
                <textarea
                  value={formData.review_text}
                  onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                  placeholder="What did the client say about your service?"
                  rows={4}
                  required
                />
              </div>

              <div className="form-group checkbox-container">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.approved}
                    onChange={(e) => setFormData({ ...formData, approved: e.target.checked })}
                  />
                  <span>Approve and show on website</span>
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? (
                    <><Loader2 size={16} className="spin" /><span>Saving...</span></>
                  ) : (
                    <><Save size={16} /><span>{editingReview ? 'Update' : 'Add'} Review</span></>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
